/**
 * MuxVideoService — the Mux implementation of {@link VideoService}.
 *
 * ⚠️ SINGLE-EDIT-SURFACE (Critical Rule 11): this is the ONLY file in the
 * codebase permitted to import `@mux/mux-node`. The `video-service-single-file`
 * verifier greps for `@mux` outside `packages/shared/src/video/` and fails the
 * build if it appears anywhere else. All business logic depends on the
 * {@link VideoService} interface, so a vendor swap is a one-file change (R7).
 *
 * Server-only: pulls the Mux SDK and secrets; never import from a client
 * component. Ingest origin is always R2 (decision D1) — this service never
 * touches the object store; raw PUTs go through `@kit/shared/storage/r2`.
 *
 * @see ./video.service.ts
 * @see .claude/clauses/critical-rules-video-architecture.md
 */
import Mux from '@mux/mux-node';

import { Result, ServiceError } from '@kit/shared/result';

import type {
  CreateVideoAssetInput,
  PlaybackTokenOptions,
  VideoAsset,
  VideoAssetStatus,
  VideoEncodingTier,
  VideoService,
  VideoWebhookEvent,
  WebhookHeaders,
} from './video.service';

/** Default signed-playback token lifetime — Rule 11 caps dancer video at 15 min. */
const DEFAULT_TOKEN_EXPIRY = '15m';

/** Mux's constrained BCP-47 language union for generated subtitles. */
type MuxCreateParams = Parameters<Mux['video']['assets']['create']>[0];
type MuxInput = NonNullable<MuxCreateParams['inputs']>[number];
type MuxSubtitleLanguage = NonNullable<
  MuxInput['generated_subtitles']
>[number]['language_code'];

export interface MuxVideoServiceConfig {
  tokenId: string;
  tokenSecret: string;
  /** Signing key id for signed playback JWTs (Mux `jwtSigningKey`). */
  signingKeyId: string;
  /** Base64 private key for signed playback JWTs (Mux `jwtPrivateKey`). */
  signingPrivateKey: string;
  /** Shared secret used to verify inbound webhook signatures. */
  webhookSecret: string;
}

/**
 * Pull the HTTP status off a vendor error. The Mux SDK throws `Mux.APIError`
 * subclasses carrying a numeric `status`; a network-level fault (aborted
 * socket, DNS failure) carries none, hence `null` rather than a guessed code.
 */
function httpStatusOf(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

/** Map Mux's asset status onto the vendor-agnostic lifecycle. */
function mapStatus(status: string | null | undefined): VideoAssetStatus {
  if (status === 'ready') return 'ready';
  if (status === 'errored') return 'errored';
  return 'preparing';
}

/**
 * Map a Mux asset's quality onto the vendor-agnostic tier. Prefer the current
 * `video_quality` field; fall back to the deprecated `encoding_tier` (older
 * assets predate `video_quality`). Returns null when neither is reported.
 */
function mapEncodingTier(asset: {
  encoding_tier?: string | null;
  video_quality?: string | null;
}): VideoEncodingTier | null {
  switch (asset.video_quality) {
    case 'basic':
      return 'baseline';
    case 'plus':
      return 'smart';
    case 'premium':
      return 'premium';
  }
  const legacy = asset.encoding_tier;
  if (legacy === 'baseline' || legacy === 'smart' || legacy === 'premium') {
    return legacy;
  }
  return null;
}

/**
 * Build a MuxVideoService from the canonical env vars.
 *
 * Required (server only):
 *   MUX_TOKEN_ID, MUX_TOKEN_SECRET,
 *   MUX_SIGNING_KEY_ID, MUX_SIGNING_PRIVATE_KEY,
 *   MUX_WEBHOOK_SECRET
 *
 * These do NOT exist yet — the Mux account + keys are a human Phase-0
 * prerequisite (BAL-721). This factory throws a descriptive error until they
 * are provisioned, so nothing silently no-ops against a half-configured vendor.
 */
export function createMuxVideoServiceFromEnv(): MuxVideoService {
  const tokenId = process.env.MUX_TOKEN_ID;
  const tokenSecret = process.env.MUX_TOKEN_SECRET;
  const signingKeyId = process.env.MUX_SIGNING_KEY_ID;
  const signingPrivateKey = process.env.MUX_SIGNING_PRIVATE_KEY;
  const webhookSecret = process.env.MUX_WEBHOOK_SECRET;
  if (
    !tokenId ||
    !tokenSecret ||
    !signingKeyId ||
    !signingPrivateKey ||
    !webhookSecret
  ) {
    throw new Error(
      'Mux env not configured. Required: MUX_TOKEN_ID, MUX_TOKEN_SECRET, ' +
        'MUX_SIGNING_KEY_ID, MUX_SIGNING_PRIVATE_KEY, MUX_WEBHOOK_SECRET',
    );
  }
  return new MuxVideoService({
    tokenId,
    tokenSecret,
    signingKeyId,
    signingPrivateKey,
    webhookSecret,
  });
}

/** Minimal shape of the Mux webhook payloads this service reacts to. */
interface MuxWebhookPayload {
  type?: string;
  data?: {
    id?: string;
    passthrough?: string | null;
    playback_ids?: Array<{ id?: string; policy?: string }> | null;
    errors?: { messages?: string[] } | null;
  };
}

export class MuxVideoService implements VideoService {
  private readonly client: Mux;
  private readonly webhookSecret: string;

  constructor(config: MuxVideoServiceConfig) {
    this.client = new Mux({
      tokenId: config.tokenId,
      tokenSecret: config.tokenSecret,
      jwtSigningKey: config.signingKeyId,
      jwtPrivateKey: config.signingPrivateKey,
      webhookSecret: config.webhookSecret,
    });
    this.webhookSecret = config.webhookSecret;
  }

  async createAssetFromUrl(
    input: CreateVideoAssetInput,
  ): Promise<Result<VideoAsset>> {
    try {
      const asset = await this.client.video.assets.create({
        inputs: [
          {
            url: input.url,
            ...(input.generateSubtitles
              ? {
                  generated_subtitles: [
                    {
                      // Mux validates the language server-side; narrow the
                      // vendor-agnostic string to its accepted union here.
                      language_code: (input.subtitleLanguage ??
                        'en') as MuxSubtitleLanguage,
                      name: `${(input.subtitleLanguage ?? 'en').toUpperCase()} (auto)`,
                    },
                  ],
                }
              : {}),
          },
        ],
        // Signed playback + short-lived JWT (Rule 11): the Mux ADAPTIVE STREAM
        // is never public — on-platform playback always mints a ≤15-min token.
        // (For SEO, PUBLIC profiles separately publish a public poster + capped
        // MP4 DERIVATIVE to R2/cdn.ballee.co — see the media-SEO derivative
        // pattern; that never makes the Mux stream itself public.)
        playback_policies: ['signed'],
        // 'plus' = Mux per-title/content-aware encoding (encoding_tier 'smart').
        // Superseded the 'basic'/baseline tier on 2026-07-13: baseline capped the
        // 1080p HLS ladder at ~3 Mbps, visibly soft on high-motion dance footage
        // vs the ~15 Mbps originals (measured). 'plus' costs only the encode fee
        // (baseline was free → ~$0.031/min@1080p); storage + delivery are
        // tier-identical, and delivery stays under Mux's 100k-free-min/mo tier at
        // current scale — so the upgrade is ~$0.03/upload, not a delivery
        // multiplier. Re-opens the Rule 11 "H.264 baseline only" lock; its cost
        // premise (delivery-scale) no longer holds. Still H.264 (no AV1/HEVC).
        // Back-catalog re-encode: WIP_re_encoding_dancer_videos_to_plus_2026_07_13.
        video_quality: 'plus',
        ...(input.passthrough ? { passthrough: input.passthrough } : {}),
      });
      return Result.ok({
        assetId: asset.id,
        status: mapStatus(asset.status),
        playbackId: asset.playback_ids?.[0]?.id ?? null,
        encodingTier: mapEncodingTier(asset),
      });
    } catch (err) {
      return Result.fail(
        ServiceError.internal(
          err instanceof Error ? err.message : 'Mux asset create failed',
          err,
        ),
      );
    }
  }

  async getAsset(assetId: string): Promise<Result<VideoAsset>> {
    try {
      const asset = await this.client.video.assets.retrieve(assetId);
      return Result.ok({
        assetId: asset.id,
        status: mapStatus(asset.status),
        playbackId: asset.playback_ids?.[0]?.id ?? null,
        encodingTier: mapEncodingTier(asset),
      });
    } catch (err) {
      // A 404 is the ONLY failure that proves the asset is genuinely gone.
      // Every other failure — 429 rate-limit, 5xx, network fault — is
      // INDETERMINATE: the asset may well exist and be perfectly healthy.
      // Callers that "repair" a missing asset by re-encoding it spend real
      // money per row, so this distinction is surfaced as the error CODE (plus
      // `details.httpStatus`) instead of leaving each caller to regex it out of
      // `message`. See `classifyAssetProbe` for the decision helper, and
      // WIP_re_encoding_dancer_videos_to_plus_2026_07_13 for the incident that
      // motivated it (a flaky probe would have re-encoded already-plus rows).
      const httpStatus = httpStatusOf(err);
      const message =
        err instanceof Error ? err.message : 'Mux asset retrieve failed';
      return Result.fail(
        new ServiceError(
          httpStatus === 404 ? 'NOT_FOUND' : 'INTERNAL_ERROR',
          message,
          err,
          {
            httpStatus,
          },
        ),
      );
    }
  }

  async deleteAsset(assetId: string): Promise<Result<void>> {
    try {
      await this.client.video.assets.delete(assetId);
      return Result.ok(undefined);
    } catch (err) {
      return Result.fail(
        ServiceError.internal(
          err instanceof Error ? err.message : 'Mux asset delete failed',
          err,
        ),
      );
    }
  }

  async verifyWebhookSignature(
    rawBody: string,
    headers: WebhookHeaders,
    secret?: string,
  ): Promise<boolean> {
    try {
      await this.client.webhooks.verifySignature(
        rawBody,
        headers as Parameters<typeof this.client.webhooks.verifySignature>[1],
        secret ?? this.webhookSecret,
      );
      return true;
    } catch {
      return false;
    }
  }

  parseWebhookEvent(rawBody: string): VideoWebhookEvent {
    let payload: MuxWebhookPayload;
    try {
      payload = JSON.parse(rawBody) as MuxWebhookPayload;
    } catch {
      return {
        type: 'other',
        assetId: null,
        playbackId: null,
        passthrough: null,
        errorMessages: [],
      };
    }
    const data = payload.data ?? {};
    const assetId = typeof data.id === 'string' ? data.id : null;
    const passthrough =
      typeof data.passthrough === 'string' ? data.passthrough : null;

    if (payload.type === 'video.asset.ready') {
      return {
        type: 'asset.ready',
        assetId,
        playbackId: data.playback_ids?.[0]?.id ?? null,
        passthrough,
        errorMessages: [],
      };
    }
    if (payload.type === 'video.asset.errored') {
      return {
        type: 'asset.errored',
        assetId,
        playbackId: null,
        passthrough,
        errorMessages: data.errors?.messages ?? [],
      };
    }
    return {
      type: 'other',
      assetId,
      playbackId: null,
      passthrough,
      errorMessages: [],
    };
  }

  async buildPlaybackToken(
    playbackId: string,
    options?: PlaybackTokenOptions,
  ): Promise<string> {
    return this.client.jwt.signPlaybackId(playbackId, {
      type: options?.type ?? 'video',
      expiration: options?.expiresIn ?? DEFAULT_TOKEN_EXPIRY,
    });
  }
}
